import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SkipBack, SkipForward, Loader2, ArrowLeft } from "lucide-react";
import { api, imgUrl } from "../lib/api";
import { EpisodeCard } from "../components/EpisodeCard";
import { Logo } from "../components/Logo";
import { saveProgress, getProgress } from "../lib/progress";
import { startView, sendProgress, startHeartbeat, stopHeartbeat } from "../lib/analytics";
import { loadIma, createAdController, getVastTags } from "../lib/imaAds";

export default function Player() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ep, setEp] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);
  const lastSave = useRef(0);
  const sessionRef = useRef(null);
  const watchedRef = useRef(0);
  const lastTickRef = useRef(null);
  const lastSentRef = useRef(0);
  const lastPercentRef = useRef(0);
  const startedForRef = useRef(null);
  const adTriggeredRef = useRef(false);
  const adContainerRef = useRef(null);
  const adControllerRef = useRef(null);
  const [adActive, setAdActive] = useState(false);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/episodes/${id}`).then((r) => setEp(r.data)).finally(() => setLoading(false));
  }, [id]);

  // Analytics: count a view (play start) and flush watch time on leave
  useEffect(() => {
    sessionRef.current = null;
    watchedRef.current = 0;
    lastTickRef.current = null;
    lastSentRef.current = 0;
    lastPercentRef.current = 0;
    if (startedForRef.current !== id) {
      startedForRef.current = id;
      startView(id).then((sid) => { sessionRef.current = sid; });
    }
    const flush = () => sendProgress(sessionRef.current, watchedRef.current, lastPercentRef.current);
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
      stopHeartbeat(); // leaving the player → drop from "Watching Now"
    };
  }, [id]);

  // Resume from saved position
  const onLoadedMeta = () => {
    const saved = getProgress(id);
    if (saved && videoRef.current && saved.currentTime < (videoRef.current.duration || 0) - 5) {
      videoRef.current.currentTime = saved.currentTime;
    }
  };

  // Create the IMA ad controller once IMA is loaded and the video/ad-container exist.
  const ensureController = () => {
    if (adControllerRef.current) return adControllerRef.current;
    if (!(window.google && window.google.ima)) return null;
    if (!videoRef.current || !adContainerRef.current) return null;
    adControllerRef.current = createAdController({
      videoEl: videoRef.current,
      adContainerEl: adContainerRef.current,
      tagUrls: getVastTags(),
      onAdStart: () => setAdActive(true),
      onResume: () => {
        setAdActive(false);
        const v = videoRef.current;
        // Resume content only if it hasn't already finished (avoid replaying the
        // whole episode if a slow no-fill resolves after playback ended).
        if (v && !v.ended) v.play().catch(() => {});
      },
    });
    return adControllerRef.current;
  };

  // Reset ad state when episode changes; load IMA and (re)create the controller.
  // Depends on `ep` so the controller is created after the video element mounts
  // (fixes a race where IMA loads before the episode fetch resolves).
  useEffect(() => {
    adTriggeredRef.current = false;
    setAdActive(false);
    let cancelled = false;
    loadIma().then(() => { if (!cancelled) ensureController(); }).catch(() => {});
    return () => {
      cancelled = true;
      if (adControllerRef.current) {
        adControllerRef.current.destroy();
        adControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ep]);

  // IMA needs a user gesture to allow ad playback; content play() is that gesture.
  const onContentPlay = () => {
    startHeartbeat(id); // actively playing → count in "Watching Now"
    const c = ensureController();
    if (c) c.initOnGesture();
  };

  // Trigger the ad when ~20 seconds remain. IMPORTANT: do NOT pause the content
  // here. We only request the ad; the content keeps playing normally. The player
  // is paused (and the ad overlay shown) ONLY when IMA fires CONTENT_PAUSE_REQUESTED,
  // i.e. an ad actually loaded and is ready to play. On a no-fill / IMA error, the
  // episode continues seamlessly with no visible pause.
  const maybeTriggerAd = () => {
    const v = videoRef.current;
    if (!v || adTriggeredRef.current) return;
    const dur = v.duration;
    if (!dur || !isFinite(dur) || dur <= 21) return; // need a meaningful final 20s
    const remaining = dur - v.currentTime;
    if (remaining > 20 || remaining <= 0) return;
    adTriggeredRef.current = true;
    const controller = ensureController();
    if (!getVastTags().length || !controller) return; // no ad configured -> keep playing
    controller.requestAd();
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !ep) return;
    const t = v.currentTime;
    // Continue Watching (position based)
    if (t - lastSave.current >= 4 || Math.abs(t - lastSave.current) > 4) {
      lastSave.current = t;
      saveProgress(ep, ep.series, t, v.duration);
    }
    // Analytics watch time (accumulate real playback, ignore seeks)
    if (lastTickRef.current != null) {
      const delta = t - lastTickRef.current;
      if (delta > 0 && delta < 2) watchedRef.current += delta;
    }
    lastTickRef.current = t;
    lastPercentRef.current = v.duration ? t / v.duration : 0;
    if (watchedRef.current - lastSentRef.current >= 10) {
      lastSentRef.current = watchedRef.current;
      sendProgress(sessionRef.current, watchedRef.current, lastPercentRef.current);
    }
    // Ad midroll near the end
    maybeTriggerAd();
  };

  const onEnded = () => {
    lastPercentRef.current = 1;
    sendProgress(sessionRef.current, watchedRef.current, 1);
    stopHeartbeat(); // finished → drop from "Watching Now"
  };

  const { prev, next } = useMemo(() => {
    if (!ep?.series_episodes) return { prev: null, next: null };
    const list = ep.series_episodes;
    const idx = list.findIndex((e) => e.id === ep.id);
    return { prev: idx > 0 ? list[idx - 1] : null, next: idx < list.length - 1 ? list[idx + 1] : null };
  }, [ep]);

  if (loading)
    return <div className="min-h-screen flex items-center justify-center bg-black"><Loader2 className="animate-spin text-blood" size={40} /></div>;

  if (!ep) return <div className="min-h-screen bg-black flex items-center justify-center text-white/50">Episode not found.</div>;

  const others = (ep.series_episodes || []).filter((e) => e.id !== ep.id);

  return (
    <div className="min-h-screen bg-black pb-24 md:pb-16">
      {/* minimal top bar */}
      <div className="flex items-center justify-between px-4 sm:px-8 h-14">
        <button onClick={() => navigate(-1)} data-testid="player-back-btn" className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold">
          <ArrowLeft size={18} /> Back
        </button>
        <button onClick={() => navigate("/")}><Logo size="text-lg" /></button>
      </div>

      {/* PLAYER */}
      <div className="w-full bg-black" data-testid="video-player-wrapper">
        <div className="max-w-[1400px] mx-auto">
          <div className="relative w-full aspect-video bg-black">
            {ep.video_url ? (
              <video
                ref={videoRef}
                key={ep.id}
                data-testid="video-element"
                src={imgUrl(ep.video_url)}
                poster={imgUrl(ep.thumbnail_url)}
                controls
                autoPlay
                playsInline
                preload="auto"
                onLoadedMetadata={onLoadedMeta}
                onTimeUpdate={onTimeUpdate}
                onEnded={onEnded}
                onPlay={onContentPlay}
                onPause={() => stopHeartbeat()}
                className="w-full h-full bg-black"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center gap-3 border border-white/5">
                {ep.thumbnail_url && <img src={imgUrl(ep.thumbnail_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
                <p className="relative text-white/60 font-display font-bold text-xl">No video source yet</p>
                <p className="relative text-white/40 text-sm">Add a video URL for this episode in the admin panel.</p>
              </div>
            )}
            <div
              ref={adContainerRef}
              data-testid="ad-container"
              className={`absolute inset-0 ${adActive ? "z-30" : "z-0 opacity-0 pointer-events-none"}`}
            />
          </div>
        </div>
      </div>

      {/* META + CONTROLS */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mt-6">
        <p className="text-xs tracking-[0.3em] uppercase font-bold text-blood glow-red">
          {ep.series?.title} · Season {ep.season_number} · Episode {ep.episode_number}
        </p>
        <h1 data-testid="player-episode-title" className="mt-2 font-display font-black text-2xl sm:text-4xl tracking-tight text-white">{ep.title}</h1>
        {ep.duration && <p className="mt-1 text-sm text-white/40">{ep.duration}</p>}
        <p className="mt-3 text-white/70 max-w-3xl leading-relaxed">{ep.description}</p>

        <div className="mt-6 flex gap-3">
          <button onClick={() => prev && navigate(`/watch/${prev.id}`)} disabled={!prev} data-testid="prev-episode-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/10 text-white font-semibold text-sm hover:bg-white/20 disabled:opacity-30 transition-colors">
            <SkipBack size={16} /> Previous
          </button>
          <button onClick={() => next && navigate(`/watch/${next.id}`)} disabled={!next} data-testid="next-episode-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-blood text-white font-semibold text-sm hover:bg-blood-hover disabled:opacity-30 transition-colors">
            Next <SkipForward size={16} />
          </button>
          <button onClick={() => navigate(`/series/${ep.series_id}`)} data-testid="player-view-series-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/10 text-white font-semibold text-sm hover:bg-white/20 transition-colors">
            View Series
          </button>
        </div>
      </div>

      {/* MORE EPISODES */}
      {others.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mt-12" data-testid="more-episodes">
          <div className="flex items-baseline gap-3 mb-5">
            <span className="w-6 h-[3px] bg-blood rounded-full glow-box" />
            <h2 className="text-xl font-display font-bold text-white">More from {ep.series?.title}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {others.map((e, i) => <EpisodeCard key={e.id} episode={e} active={false} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
